/**
 * A Display's look rows in the Inspector (DEC-319, built 2026-10-27), grouped as DEC-318 groups the grid's:
 * Style | Label, its icon, Font, Align, the timeline's name line | Show (detail line, beat dots) | Glow.
 * Colour is the Inspector's own swatch row (Auto = none). Words are the study's (`public/items-look-study.html`).
 */

import { For, Show } from 'solid-js';
import type { DisplayItem } from '../layout/projectStore.ts';
import { editItem } from '../layout/store.ts';
import { Tip } from './Tip.tsx';
import type { DisplayLabelAt, DisplayAlign, DisplaySongAlign } from '../layout/displayLook.ts';

const STYLES: [NonNullable<DisplayItem['style']>, string][] = [['skin', 'Skin'], ['lcd', 'LCD'], ['glass', 'Glass']];
const LABELS: [DisplayLabelAt, string][] = [
  ['tl', 'Top left'], ['tc', 'Top centre'], ['tr', 'Top right'], ['bl', 'Bottom left'], ['bc', 'Bottom centre'], ['br', 'Bottom right'], ['off', 'Off'],
];
const ALIGN: [DisplayAlign, string][] = [['left', 'Left'], ['center', 'Centre']];
const SONG_ALIGN: [DisplaySongAlign, string][] = [['left', 'Left'], ['center', 'Centre'], ['right', 'Right']];

function Pills<T extends string>(props: { label: string; field: string; options: [T, string][]; value: T; onPick: (v: T) => void; tip?: string }) {
  return (
    <div class="inspector-zone">
      <div class="inspector-pills" data-look-field={props.field}>
        <span class="inspector-pills-label">{props.label}<Show when={props.tip}><Tip text={props.tip ?? ''} /></Show></span>
        <For each={props.options}>
          {([v, l]) => (
            <button type="button" class="inspector-pill" data-value={v} data-selected={props.value === v ? 'true' : 'false'} onClick={() => props.onPick(v)}>{l}</button>
          )}
        </For>
      </div>
    </div>
  );
}

/** With the colour: the screen's style. */
export function DisplayStyleRow(props: { item: DisplayItem }) {
  return (
    <Pills
      field="dstyle" label="Style" options={STYLES} value={props.item.style ?? 'skin'}
      onPick={(v) => editItem(props.item.id, { style: v })}
      tip="Skin follows the app's skin; LCD is a fixed green; Glass lights the screen in its colour."
    />
  );
}

/** With the name and font: the label on the bezel, its icon, the big line's alignment, a timeline's name line. */
export function DisplayTextRows(props: { item: DisplayItem }) {
  const it = () => props.item;
  const timeline = () => it().source === 'timeline';
  return (
    <>
      <div class="inspector-zone">
        <label class="inspector-field" data-look-field="labelAt">
          <span>Label<Tip text="Printed on the bezel: the kind of thing it shows, then the source (TIMELINE · Now playing)." /></span>
          <select value={it().labelAt ?? 'tl'} onChange={(e) => editItem(it().id, { labelAt: e.currentTarget.value as DisplayLabelAt })}>
            <For each={LABELS}>{([v, l]) => <option value={v}>{l}</option>}</For>
          </select>
        </label>
      </div>
      <Pills field="labelIcon" label="Label icon" options={[['on', 'On'], ['off', 'Off']]} value={it().labelIcon === false ? 'off' : 'on'}
        onPick={(v) => editItem(it().id, { labelIcon: v === 'on' })} />
      <Pills field="align" label="Align" options={ALIGN} value={it().align ?? 'left'} onPick={(v) => editItem(it().id, { align: v })} />
      <Show when={timeline()}>
        <Pills field="songAlign" label="Song name" options={SONG_ALIGN} value={it().songAlign ?? 'left'}
          onPick={(v) => editItem(it().id, { songAlign: v })} tip="The timeline's name, always on its own line." />
      </Show>
    </>
  );
}

/** Its own group: what shows, and the glow. */
export function DisplayShowRows(props: { item: DisplayItem }) {
  const it = () => props.item;
  const np = () => it().source === 'nowplaying';
  return (
    <>
      <Show when={!np()}>
        <div class="inspector-zone inspector-toggle-row">
          <label class="inspector-toggle" data-look-field="showDetail">
            <input type="checkbox" checked={it().showDetail !== false} onInput={(e) => editItem(it().id, { showDetail: e.currentTarget.checked })} />
            <span>Detail line</span>
          </label>
          <label class="inspector-toggle" data-look-field="showDots">
            <input type="checkbox" checked={!!it().showDots} onInput={(e) => editItem(it().id, { showDots: e.currentTarget.checked })} />
            <span>Beat dots</span>
          </label>
        </div>
      </Show>
      <div class="inspector-zone">
        <label class="inspector-field" data-look-field="glow">
          <span>Glow</span>
          <input type="range" min="0" max="100" step="5" value={it().glow ?? 100}
            onInput={(e) => editItem(it().id, { glow: Math.max(0, Math.min(100, Number(e.currentTarget.value))) })} />
          <b class="inspector-range-val">{it().glow ?? 100}%</b>
        </label>
      </div>
    </>
  );
}
